import { faEdit, faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Column, DataTable, DataTableContainer, TableActionButton } from 'components/DataTable';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateForm, TemplatePreview } from '../TemplateForm';

import { Button } from '@ui';
import { MessageTemplate } from 'components/MessageManagement/types';
import { useModal } from 'hooks/useModal';
import moment from 'moment';

interface Props {
    clubSlug: string;
}

export const TemplateList = ({ clubSlug }: Props) => {
    const { t } = useTranslation('message-management');
    const [setModal, resetModal] = useModal();
    const asyncList = useRef(null);

    const columns: Column[] = useMemo(() => [
        { name: '#', uid: 'id', allowsSorting: true },
        { name: t('template.list.columns.title'), uid: 'title', allowsSorting: true },
        {
            name: t('template.list.columns.sentAt'),
            uid: 'created_at',
            allowsSorting: true,
            renderer(value) {
                return moment(value).format('lll');
            }
        },
        { name: t('template.list.columns.actions'), uid: 'actions' }
    ], [t]);

    const openTemplateFormHandler = (template?: MessageTemplate) =>
        setModal({
            title: template
                ? t('template.list.editTitle', { title: template.title })
                : t('template.list.createTitle'),
            content: (
                <TemplateForm
                    template={template}
                    clubSlug={clubSlug}
                    callback={(nt) => {
                        if (!template) asyncList.current?.append(nt);
                        else asyncList.current?.update(nt.id, nt);

                        resetModal();
                    }}
                />
            ),
            show: true,
            size: 100
        });

    const openTemplatePreviewHandler = (template: MessageTemplate) =>
        setModal({
            title: t('template.list.previewTitle', { title: template.title }),
            content: <TemplatePreview css={{ minHeight: '70vh' }} template={template} withoutWrapper />,
            show: true,
            size: 70
        });

    return (
        <DataTableContainer>
            <Button className="tw-py-5" onPress={() => openTemplateFormHandler()}>
                {t('template.list.create')}
            </Button>
            <DataTable
                tableProps={{
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false
                }}
                hideSearchBar
                asyncListRef={asyncList}
                rowActions={(item) => (
                    <TableActionButton.Container>
                        <TableActionButton
                            onPress={() => openTemplatePreviewHandler(item)}
                            icon={faEye}
                            tooltip={t('template.list.previewTooltip')}
                        />
                        <TableActionButton
                            onPress={() => openTemplateFormHandler(item)}
                            icon={faEdit}
                            tooltip={t('common.edit')}
                        />
                        <TableActionButton icon={faTrash} tooltip={t('common.delete')} disabled />
                    </TableActionButton.Container>
                )}
                columns={columns}
                fetchUrl={`message-management/template/${clubSlug}`}
            />
        </DataTableContainer>
    );
};
